import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useQueryClient } from '@tanstack/react-query';
// import { DatalistInput } from '../components/dataListInput';
// import { useSettings } from '../../../context/SettingContext';
import { PageLayoutWrapper } from '../../../components/FormPageLayoutWrapper';
import { SignatureModal } from '../../../components/Form/SignatureModel';
// import { AttachmentSection } from '../../../components/Form/AttachmentSection';
import { FormActionButtons } from '../../../components/Form/FormActionButton';
import type { GeoLevel } from '../../../types/database';
import type { User } from '../../../types/user';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import type { LegalAdviceFormEightAData } from '../../../types/LegalAdvices/LegalAdviceFormEightA.type';
import { fetchLegalAdviceById, saveLegalAdviceDraft, submitLegalAdviceForm } from '../../../services/LegalAdviceFormEightAService';

// import { FormDatePicker } from '../../../components/Form/FormDatePicker';

export const INITIAL_LEGAL_ADVICE_FORM_EIGHT_A_DATA: LegalAdviceFormEightAData = {
  id: '',
  documentNumber: '',
  status: 'DRAFT',
  rejectionReason: '',
  submitDate: '',
  attachments: [],

  // Legal Advice Form Three specific fields
  office: '',
    scrutinyCaseNo: '',
    appealRegisterNo: '',
    submittingOfficeName: '',
    highCourtDetails: '',
    originalCourtDetails: '',
    chargedSection: '',
    originalJudgmentDate: '',
    appealCourtDetails: '',
    appealDeadlineDate: '',
    initialOfficerOpinionProsecution: '',
    initialOfficerOpinionDefense: '',
    jointOfficerOpinionFitness: '',
    initialOfficerSignName: '',
    initialOfficerRank: '',
    initialOfficerReceiveDate: '',
    initialOfficerSubmitDate: '',
    initialOfficerSignatureImage: '',
    directorOpinion: '',
    directorSignName: '',
    directorReceiveDate: '',
    directorSubmitDate: '',
    directorSignatureImage: '',
    deputyDirectorGeneralOpinion: '',
    deputyDirectorGeneralSignName: '',
    deputyDirectorGeneralReceiveDate: '',
    deputyDirectorGeneralSubmitDate: '',
    deputyDirectorGeneralSignatureImage: '',
    directorGeneralOpinion: '',
    directorGeneralSignName: '',
    directorGeneralReceiveDate: '',
    directorGeneralSubmitDate: '',
    directorGeneralSignatureImage: '',
    deputyAttorneyGeneralOpinion: '',
    deputyAttorneyGeneralSignName: '',
    deputyAttorneyGeneralSubmitDate: '',
    deputyAttorneyGeneralSignatureImage: '',
    unionAttorneyGeneralOpinion: '',
    unionAttorneyGeneralSignName: '',
    unionAttorneyGeneralSubmitDate: '',
    unionAttorneyGeneralSignatureImage: '',
};

interface LegalFormProps {
  id?: string;
  initialData?: Partial<LegalAdviceFormEightAData>;
}

export function LegalFormEightA({ id: propId, initialData }: LegalFormProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  const id = propId || params.id;
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const [formData, setFormData] = useState<LegalAdviceFormEightAData>({
    ...INITIAL_LEGAL_ADVICE_FORM_EIGHT_A_DATA,
    ...initialData,
  });
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [error, setError] = useState<string | null>(null);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean>(
    initialData?.status === 'APPROVED' || formData.status === 'APPROVED'
  );
   const [selectedGeoLevels, setSelectedGeoLevels] = useState<GeoLevel[]>([]);
  const [activeSigningRole, setActiveSigningRole] = useState<
    'director' | 'initial' | 'deputyDG' | 'directorGeneral' | 'deputyAttorneyGeneral' | 'unionAttorneyGeneral' | null
  >(null);

  // const { settings } = useSettings();
  // const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];
  // const TOWNSHIP_OPTIONS = settings?.townships.map((t) => t.name) || [];
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null') as User | null

  useEffect(() => {
    if (formData.status === 'APPROVED') {
      setIsApproved(true);
    }
  }, [formData.status]);

  // Fetch form data by ID
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchLegalAdviceById(id)
      .then((data: LegalAdviceFormEightAData) => {
        if (isSubscribed) {
          setFormData((prev) => ({
            ...prev,
            ...data,
            id: data.id || prev.id || id || '',
          }));
          if (data.status === 'APPROVED') {
            setIsApproved(true);
          }
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Form is editable ONLY if status is DRAFT/RETURNED AND it has NOT been approved yet
  const isEditable = (formData.status === 'DRAFT') && !isApproved;

  const requiredSigners = [
    {
      label: 'ကနဦးစိစစ်သည့် ဥပဒေအရာရှိ',
      name: formData.initialOfficerSignName,
      signature: formData.initialOfficerSignatureImage,
    },
    {
      label: 'ညွှန်ကြားရေးမှူး',
      name: formData.directorSignName,
      signature: formData.directorSignatureImage,
    },
    {
      label: 'ဒုတိယညွှန်ကြားရေးမှူးချုပ်',
      name: formData.deputyDirectorGeneralSignName,
      signature: formData.deputyDirectorGeneralSignatureImage,
    },
  ];
  const missingRequiredSigners = requiredSigners.filter(
    (signer) => !signer.name?.trim() || !signer.signature?.trim()
  );
  const isFormSigned = missingRequiredSigners.length === 0;

  const validateRequiredSignatures = () => {
    if (isFormSigned) return true;

    const missingSigners = missingRequiredSigners
      .map((signer) => signer.label)
      .join('၊ ');
    alert(`လက်မှတ်နှင့် အမည် ဖြည့်သွင်းရန် လိုအပ်ပါသည်။ (${missingSigners})`);
    return false;
  };

  // Navigation Handler
  const handleBackNavigation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['legalFormEight'] });
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/legal-form-one');
    }
  };

  // Form Field Event Handlers
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearDate = (name: keyof LegalAdviceFormEightAData) => {
      if (!isEditable) return;
      setFormData((prev) => ({ ...prev, [name]: '' }));
    };


  const renderDatePicker = (
    name: Extract<keyof LegalAdviceFormEightAData, string>
  ) => (
    <FormDatePicker
      name={name}
      value={(formData?.[name] as string) || ''}
      isEditable={isEditable}
      onChange={handleChange}
      onClear={handleClearDate}
    />
  );

  // const handleClearDate = (name: keyof LegalAdviceFormEightAData) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({ ...prev, [name]: '' }));
  // };

  const handleClearInitialSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, initialOfficerSignatureImage: '' }));
  };

  const handleClearDirectorSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, directorSignatureImage: '' }));
  };

  const handleClearDeputySignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, deputyDirectorGeneralSignatureImage: '' }));
  };

  const handleClearDirectorGeneralSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, directorGeneralSignatureImage: '' }));
  };

  const handleClearDeputyAttorneyGeneralSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, deputyAttorneyGeneralSignatureImage: '' }));
  };

  const handleClearUnionAttorneyGeneralSignature = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditable) return;
    setFormData((prev) => ({ ...prev, unionAttorneyGeneralSignatureImage: '' }));
  };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!isEditable || !e.target.files) return;
  //   const filesArray = Array.from(e.target.files);
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: [...(prev.attachments || []), ...filesArray],
  //   }));
  // };

  // const handleRemoveFile = (index: number) => {
  //   if (!isEditable) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     attachments: (prev.attachments || []).filter((_, i) => i !== index),
  //   }));
  // };


  const handleOpenSignatureModal = (
    role: NonNullable<typeof activeSigningRole>
  ) => {
    if (!isEditable) return;
    setActiveSigningRole(role);
    setIsSigning(true);
  };


  const handleSaveSignature = () => {
    if (!sigCanvasRef.current || !activeSigningRole) return;
    if (sigCanvasRef.current.isEmpty()) {
      alert('လက်မှတ်ရေးထိုးပေးပါရန်။');
      return;
    }

    const dataUrl = sigCanvasRef.current.getCanvas().toDataURL('image/png');
    const signatureFieldByRole = {
      initial: 'initialOfficerSignatureImage',
      director: 'directorSignatureImage',
      deputyDG: 'deputyDirectorGeneralSignatureImage',
      directorGeneral: 'directorGeneralSignatureImage',
      deputyAttorneyGeneral: 'deputyAttorneyGeneralSignatureImage',
      unionAttorneyGeneral: 'unionAttorneyGeneralSignatureImage',
    } as const;
    const signatureField = signatureFieldByRole[activeSigningRole];

    setFormData((prev) => ({ ...prev, [signatureField]: dataUrl }));
    setIsSigning(false);
    setActiveSigningRole(null);
  };

  const handleSaveDraft = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isEditable || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const payload: Partial<LegalAdviceFormEightAData> = { ...formData, status: 'DRAFT' };

      const response = await saveLegalAdviceDraft(formData.id || id || null, payload);

      if (response && response.id) {
        setFormData((prev) => ({ ...prev, id: response.id, status: 'DRAFT' }));
      }

      alert('မူကြမ်း သိမ်းဆည်းပြီးပါပြီ (Draft Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      alert(err?.message || 'မူကြမ်း သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Approve & Save (အတည်ပြုသိမ်းဆည်းမည်)
  const handleApproveAndSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (
      !formData.initialOfficerSubmitDate?.trim() ||
      !formData.directorSubmitDate?.trim() ||
      !formData.deputyDirectorGeneralSubmitDate?.trim() ||
      !formData.directorGeneralSubmitDate?.trim() ||
      !formData.deputyAttorneyGeneralSubmitDate?.trim() ||
      !formData.unionAttorneyGeneralSubmitDate?.trim()
    ) {
      alert('တင်ပြသည့်ရက်စွဲများ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

    if (!validateRequiredSignatures()) return;

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      const payload: LegalAdviceFormEightAData = {
        ...formData,
        id: targetId || '',
        // officerName: nameOfOfficer,
        // prosecutorName: nameOfProsecutor,
        // status: 'APPROVED',
      };

      const response = await saveLegalAdviceDraft(targetId || null, payload);
      const newId = response?.id || targetId;

      setFormData((prev) => ({
        ...prev,
        id: newId,
        // officerName: nameOfOfficer,
        // prosecutorName: nameOfProsecutor,
        status: 'APPROVED',
      }));

      setIsApproved(true); // Locks form editing
      alert('အတည်ပြု သိမ်းဆည်းပြီးပါပြီ (Approved & Saved Successfully)');
    } catch (err: any) {
      console.error('Failed to approve and save:', err);
      alert(err?.message || 'အတည်ပြု သိမ်းဆည်းခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handler for Submit To Superior (အထက်သို့ တင်ပြမည်)
  const handleSubmitToSuperior = async (e: React.MouseEvent,
    targetLevels?: GeoLevel[] | null
  ) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (
      !formData.initialOfficerSubmitDate?.trim() ||
      !formData.directorSubmitDate?.trim() ||
      !formData.deputyDirectorGeneralSubmitDate?.trim() ||
      !formData.directorGeneralSubmitDate?.trim() ||
      !formData.deputyAttorneyGeneralSubmitDate?.trim() ||
      !formData.unionAttorneyGeneralSubmitDate?.trim()
    ) {
      alert('တင်ပြသည့်ရက်စွဲများ ဖြည့်သွင်းပေးပါရန်။');
      return;
    }

     // Use passed levels if available; otherwise use multi-select state
    const levelsToSubmit =
      targetLevels && targetLevels.length > 0 ? targetLevels : selectedGeoLevels;

    // Validate that at least one checkbox level is selected
    if (!levelsToSubmit || levelsToSubmit.length === 0) {
      alert('အထက်သို့ တင်ပြရန် အဆင့် (Level) အနည်းဆုံးတစ်ခု ရွေးချယ်ပေးပါရန်။');
      return;
    }

    if (!validateRequiredSignatures()) return;

    try {
      setIsSubmitting(true);
      let targetId: string | undefined = formData.id || id;

      if (!targetId) {
        const draftResponse = await saveLegalAdviceDraft(null, { ...formData, status: 'DRAFT' });
        targetId = draftResponse?.id;
        if (!targetId) {
          throw new Error('အချက်အလက် သိမ်းဆည်းခြင်း အိုင်ဒီ (ID) မရရှိပါ');
        }

        setFormData((prev) => ({
          ...prev,
          ...draftResponse,
          id: targetId!,
        }));
      }

      const payload: LegalAdviceFormEightAData = {
        ...formData,
        id: targetId,
        // officerName: nameOfOfficer,
        // prosecutorName: nameOfProsecutor,
        status: 'SUBMITTED',
        submitDate: formData.deputyDirectorGeneralSubmitDate,
        targetGeoLevels: levelsToSubmit, // Array of selected levels
      };

      const response = await submitLegalAdviceForm(targetId, payload);

      setFormData((prev) => ({
        ...prev,
        id: targetId,
        status: response?.status || 'SUBMITTED',
      }));

      setIsApproved(true); // Locks form editing after submission
      alert(
        response?.status ==='SUBMITTED'
          ?  'အထက်သို့ တင်ပြပြီးပါပြီ (Submitted Successfully)' : 'တင်ပြမှုမအောင်မြင်ပါ'
      );
      navigate('/legal-form-one');
    } catch (err: any) {
      console.error('Failed to submit form:', err);
      alert(err?.message || 'တင်ပြခြင်း မအောင်မြင်ပါ။ ပြန်လည် ကြိုးစားပါ။');
    } finally {
      setIsSubmitting(false);
    }
  };

  const GEO_LEVEL_MAP: Record<string, string> = {
    NATIONAL: 'ရုံးချုပ်',
    STATE: 'ပြည်နယ်/တိုင်း',
    DISTRICT: 'ခရိုင်',
    TOWNSHIP: 'မြို့နယ်',
  };

  const renderStageProgression = () => {
  const status = formData.status || 'DRAFT';

  // Extract and format Geo Level text
  const rawGeo = formData.targetGeoLevels || formData.geoLevel;
  const formatGeo = (geo: any) => {
    if (typeof geo === 'string') return GEO_LEVEL_MAP[geo] || geo;
    if (Array.isArray(geo)) {
      return geo
        .map((g) => GEO_LEVEL_MAP[g] || g)
        .filter(Boolean)
        .join(' | ');
    }
    return '';
  };
  const geoText = formatGeo(rawGeo);

  const STATUS_CONFIG: Record<
    string,
    { label: string; bg: string; text: string; border: string; dot: string }
  > = {
    DRAFT: {
      label: 'မူကြမ်း',
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      border: 'border-slate-200',
      dot: 'bg-slate-400',
    },
    APPROVED: {
      label: 'ကိုယ်တိုင်အတည်ပြုပြီး',
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-600',
    },
    SUBMITTED: {
      label: 'တင်ပြပြီး',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
    },
    UNDER_REVIEW: {
      label: 'ကြည့်ရှုထားသည်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
    ATTACHED: {
      label: 'ပြင်ဆင်ချက်/ညွှန်ကြားချက်',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      dot: 'bg-blue-500',
    },
  };

    const currentConfig = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;

      if (loading) {
        return (
          <div className="flex items-center justify-center p-12 text-slate-500">
            <span className="animate-spin mr-2">⏳</span> အချက်အလက်များ ရယူနေပါသည်...
          </div>
        );
      }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2.5 px-3.5 bg-slate-50 border border-slate-200 rounded-lg shadow-xs">
        {/* Left: Section Title & Location Subtext */}
        <div className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            လုပ်ငန်းစဉ် အခြေအနေ
          </span>
          {geoText && (
            <span className="text-xs text-slate-600 font-medium mt-0.5 flex items-center gap-1">
              <span className="text-slate-400">📍</span>
              <span>{geoText}</span>
            </span>
          )}
        </div>

        {/* Right: Modern Pill Badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentConfig.bg} ${currentConfig.text} ${currentConfig.border} self-start sm:self-center`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
          <span>{currentConfig.label}</span>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 rounded-xl border border-rose-200 my-6 max-w-xl mx-auto">
        <p className="font-semibold">{error}</p>
        <button
          type="button"
          onClick={handleBackNavigation}
          className="mt-4 px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
        >
          နောက်သို့ ပြန်သွားမည်
        </button>
      </div>
    );
  }

  return (
      <PageLayoutWrapper
        handleBackNavigation={handleBackNavigation}
        renderStageProgression={renderStageProgression}
        isEditable={isEditable}
        isPreview={isPreview}
        readOnlyMessage={
          formData.status === 'APPROVED'
            ? 'စာရွက်စာတမ်းကို ကိုယ်တိုင်အတည်ပြုပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
            : formData.status === 'SUBMITTED'
              ? 'စာရွက်စာတမ်းကို အထက်သို့ တင်ပြပြီးဖြစ်သဖြင့် ပြင်ဆင်၍ မရပါ'
              : undefined
        }
        setIsPreview={setIsPreview}
      >
        {/* A4 Sheet Container */}
        {/* Main A4 Document Form Canvas */}
      <div className="w-full max-w-[850px] bg-white shadow-lg border border-gray-300 p-12 text-black space-y-14 text-xs md:text-sm leading-relaxed">
        
        {/* ခေါင်းစီးပိုင်း */}
        <div>
          <div className="text-right text-xs font-bold">ဥပရအမှုလိုက်ပုံစံ (၈-က)</div>
          
          <div className="text-center space-y-2 mt-2">
            <h1 className="text-base font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>
            <h2 className="text-sm font-bold">ပြည်ထောင်စုရှေ့နေချုပ်ရုံး</h2>
            <h2 className="text-sm font-bold">နေပြည်တော်</h2>
            
            <h3 className="text-sm font-bold pt-4 max-w-xl mx-auto leading-normal">
              အပြီးအပြတ်လွှတ်သည့်အမိန့်ကို အယူခံဝင်ရန် သင့် မသင့် <br />
              ပြည်ထောင်စုရှေ့နေချုပ်ရုံးက စိစစ်ခြင်း
            </h3>
          </div>

          {/* အပိုင်း ၁။ စိစစ်ချက်အချက်အလက်များ (၁ မှ ၉) */}
          <div className="mt-8 space-y-4">
            <div className="flex items-baseline">
              <span className="w-10 font-bold">၁။</span>
              <span className="flex-1">စိစစ်သည့် အမှုတွဲအမှတ်</span>
              <input type="text" name="scrutinyCaseNo" value={formData.scrutinyCaseNo} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၂။</span>
              <span className="flex-1">အယူခံဝင်ရန်သင့် မသင့် စိစစ်ခြင်းမှတ်ပုံတင်အမှတ်စဉ်</span>
              <input type="text" name="appealRegisterNo" value={formData.appealRegisterNo} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၃။</span>
              <span className="flex-1">တင်ပြသည့် တိုင်းဒေသကြီး/ ပြည်နယ်ဥပဒေချုပ်ရုံးအမည်</span>
              <input type="text" name="submittingOfficeName" value={formData.submittingOfficeName} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၄။</span>
              <span className="flex-1">တရားလွှတ်တော်အမည်၊ အမှုအမှတ်၊ အမိန့်၊ ရက်စွဲ</span>
              <input type="text" name="highCourtDetails" value={formData.highCourtDetails} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၅။</span>
              <span className="flex-1">မူလရုံး အမှုအမှတ်၊ တရားစွဲဆိုသည့်ဥပဒေ ပုဒ်မ</span>
              <input type="text" name="originalCourtDetails" value={formData.originalCourtDetails} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၆။</span>
              <span className="flex-1">စွဲချက်တင်သည့် ဥပဒေ ၊ ပုဒ်မ</span>
              <input type="text" name="chargedSection" value={formData.chargedSection} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၇။</span>
              <span className="flex-1">မူလရုံးအမိန့်၊ ရက်စွဲ</span>
              <div className="flex-grow">{renderDatePicker('originalJudgmentDate')}</div>
              {/* <input type="text" name="originalJudgmentDate" value={formData.originalJudgmentDate} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" /> */}
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၈။</span>
              <span className="flex-1">အယူခံ၊ ပြင်ဆင်ရုံး၏ အမှုအမှတ်၊ အမိန့်၊ ရက်စွဲ</span>
              <input type="text" name="appealCourtDetails" value={formData.appealCourtDetails} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>

            <div className="flex items-baseline">
              <span className="w-10 font-bold">၉။</span>
              <span className="flex-1">အယူခံဝင်ရန် ကာလစည်းကမ်းသတ် ကုန်ဆုံးသည့်ရက်စွဲ</span>
              <div className="flex-grow">{renderDatePicker('appealDeadlineDate')}</div>
              <input type="text" name="appealDeadlineDate" value={formData.appealDeadlineDate} onChange={handleChange} className="w-1/3 bg-transparent border-b border-dotted border-black focus:outline-none px-2 text-center" />
            </div>
          </div>
        </div>
{/* 
        <hr className="border-gray-300" /> */}

        {/* အပိုင်း ၂။ ကနဦး စိစစ်သည့် ဥပဒေအရာရှိ၏ သုံးသပ်ချက် (၁၀) */}
        <div className="space-y-4">
          <div className="flex flex-col space-y-1">
            <label className="font-bold"><span className="mr-4">၁၀။</span>ကနဦး စိစစ်သည့် ဥပဒေအရာရှိ၏ သုံးသပ်တင်ပြချက်</label>
            <div className="pl-6 space-y-3 mt-2">
              <div className="flex flex-col space-y-1">
                <span className="font-medium">(က) တရားလိုဘက်မှ တင်ပြချက်များ အပေါ် သုံးသပ်ချက်</span>
                <textarea name="initialOfficerOpinionProsecution" value={formData.initialOfficerOpinionProsecution} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
              </div>
              <div className="flex flex-col space-y-1">
                <span className="font-medium">(ခ) တရားခံဘက်မှ တင်ပြချက်များ အပေါ် သုံးသပ်ချက်</span>
                <textarea name="initialOfficerOpinionDefense" value={formData.initialOfficerOpinionDefense} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
              </div>
              <div className="flex flex-col space-y-1">
                <span className="font-medium">(ဂ) နှစ်ဖက်တင်ပြချက်များ နှင့်စပ်လျဉ်း၍ အယူခံဝင် သင့် မသင့် သုံးသပ်တင်ပြချက်</span>
                <textarea name="jointOfficerOpinionFitness" value={formData.jointOfficerOpinionFitness} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
              </div>
            </div>
          </div>

          {/* ကနဦးစိစစ်သူ လက်မှတ်ကွက် */}
          <div className="flex flex-col items-end pt-4">
            <div className="w-80 space-y-2">
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">လက်မှတ် </span>
                <div onClick={() => handleOpenSignatureModal('initial')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.initialOfficerSignatureImage ? (
                    <img src={formData.initialOfficerSignatureImage} alt="Initial Officer Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.initialOfficerSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearInitialSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">အမည် </span>
                <input type="text" name="initialOfficerSignName" value={formData.initialOfficerSignName} onChange={handleChange} required={isEditable} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">ရာထူး</span>
                <input type="text" name="initialOfficerRank" value={formData.initialOfficerRank} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">လက်ခံရရှိသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('initialOfficerReceiveDate')}</div>
                {/* <input type="text" name="initialOfficerReceiveDate" value={formData.initialOfficerReceiveDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('initialOfficerSubmitDate')}</div>
                {/* <input type="text" name="initialOfficerSubmitDate" value={formData.initialOfficerSubmitDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
            </div>
          </div>
        </div>


        {/* ညွှန်ကြားရေးမှူး၏ သဘောထားမှတ်ချက် */}
        <div className="space-y-4">
          <div className="flex flex-col space-y-1">
           <label className="font-bold"><span className="mr-4">၁၁။</span>ညွှန်ကြားရေးမှူး၏ သဘောထားမှတ်ချက်</label>
            <textarea name="directorOpinion" value={formData.directorOpinion} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          <div className="flex flex-col items-end pt-2">
            <div className="w-80 space-y-2">
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">လက်မှတ်</span>
                <div onClick={() => handleOpenSignatureModal('director')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.directorSignatureImage ? (
                    <img src={formData.directorSignatureImage} alt="Director Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.directorSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearDirectorSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">အမည် </span>
                <input type="text" name="directorSignName" value={formData.directorSignName} onChange={handleChange} required={isEditable} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="text-center text-xs font-bold pt-1">ညွှန်ကြားရေးမှူး</div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">လက်ခံရရှိသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('directorReceiveDate')}</div>
                {/* <input type="text" name="directorReceiveDate" value={formData.directorReceiveDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('directorSubmitDate')}</div>
                {/* <input type="text" name="directorSubmitDate" value={formData.directorSubmitDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
            </div>
          </div>
        </div>

        {/* ဒုတိယညွှန်ကြားရေးမှူးချုပ်၏ သဘောထားမှတ်ချက် */}
        <div className="space-y-4">
          <div className="flex flex-col space-y-1">
           <label className="font-bold"><span className="mr-4">၁၂။</span>ဒုတိယညွှန်ကြားရေးမှူးချုပ်၏ သဘောထားမှတ်ချက်</label>
            <textarea name="deputyDirectorGeneralOpinion" value={formData.deputyDirectorGeneralOpinion} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          <div className="flex flex-col items-end pt-2">
            <div className="w-80 space-y-2">
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">လက်မှတ်</span>
                <div onClick={() => handleOpenSignatureModal('deputyDG')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.deputyDirectorGeneralSignatureImage ? (
                    <img src={formData.deputyDirectorGeneralSignatureImage} alt="Deputy Director General Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.deputyDirectorGeneralSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearDeputySignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">အမည် *</span>
                <input type="text" name="deputyDirectorGeneralSignName" value={formData.deputyDirectorGeneralSignName} onChange={handleChange} required={isEditable} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="text-center text-xs font-bold pt-1">ဒုတိယညွှန်ကြားရေးမှူးချုပ်</div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">လက်ခံရရှိသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('deputyDirectorGeneralReceiveDate')}</div>
              </div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('deputyDirectorGeneralSubmitDate')}</div>
              </div>
            </div>
          </div>
        </div>

        {/*ညွှန်ကြားရေးမှူးချုပ် */}
        <div className="space-y-4">
          <div className="flex flex-col space-y-1">
           <label className="font-bold"><span className="mr-4">၁၃။</span>ညွှန်ကြားရေးမှူးချုပ်၏ သဘောထားမှတ်ချက်</label>
            <textarea name="directorGeneralOpinion" value={formData.directorGeneralOpinion} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          <div className="flex flex-col items-end pt-2">
            <div className="w-80 space-y-2">
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">လက်မှတ်</span>
                <div onClick={() => handleOpenSignatureModal('directorGeneral')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.directorGeneralSignatureImage ? (
                    <img src={formData.directorGeneralSignatureImage} alt="Director General Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.directorGeneralSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearDirectorGeneralSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">အမည်</span>
                <input type="text" name="directorGeneralSignName" value={formData.directorGeneralSignName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="text-center text-xs font-bold pt-1">ညွှန်ကြားရေးမှူးချုပ်</div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">လက်ခံရရှိသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('directorGeneralReceiveDate')}</div>
                {/* <input type="text" name="deputyDirectorGeneralReceiveDate" value={formData.deputyDirectorGeneralReceiveDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('directorGeneralSubmitDate')}</div>
              </div>
            </div>
          </div>
        </div>


        {/*ဒုတိယရှေ့နေချုပ် */}
        <div className="space-y-4">
          <div className="flex flex-col space-y-1">
           <label className="font-bold"><span className="mr-4">၁၄။</span>ဒုတိယရှေ့နေချုပ်၏ အယူခံဝင်ရန် သင့် မသင့် ဆုံးဖြတ်ချက်</label>
            <textarea name="deputyAttorneyGeneralOpinion" value={formData.deputyAttorneyGeneralOpinion} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          <div className="flex flex-col items-end pt-2">
            <div className="w-80 space-y-2">
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">လက်မှတ်</span>
                <div onClick={() => handleOpenSignatureModal('deputyAttorneyGeneral')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.deputyAttorneyGeneralSignatureImage ? (
                    <img src={formData.deputyAttorneyGeneralSignatureImage} alt="Deputy Attorney General Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.deputyAttorneyGeneralSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearDeputyAttorneyGeneralSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">အမည်</span>
                <input type="text" name="deputyAttorneyGeneralSignName" value={formData.deputyAttorneyGeneralSignName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="text-center text-xs font-bold pt-1">ဒုတိယရှေ့နေချုပ်</div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">ဆုံးဖြတ်သည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('deputyAttorneyGeneralSubmitDate')}</div>
              </div>
            </div>
          </div>
        </div>


         {/*ပြည်ထောင်စုရှေ့နေချုပ်၏ ဆုံးဖြတ်ချက် */}
         <div className="space-y-4">
          <div className="flex flex-col space-y-1">
           <label className="font-bold"><span className="mr-4">၁၅။</span>ပြည်ထောင်စုရှေ့နေချုပ်၏ ဆုံးဖြတ်ချက် </label>
            <textarea name="unionAttorneyGeneralOpinion" value={formData.unionAttorneyGeneralOpinion} onChange={handleChange} rows={4} className="w-full bg-transparent border border-gray-300 rounded p-2 focus:outline-none" />
          </div>

          <div className="flex flex-col items-end pt-2">
            <div className="w-80 space-y-2">
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">လက်မှတ်</span>
                <div onClick={() => handleOpenSignatureModal('unionAttorneyGeneral')} className="flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center cursor-pointer hover:bg-gray-50 relative">
                  {formData.unionAttorneyGeneralSignatureImage ? (
                    <img src={formData.unionAttorneyGeneralSignatureImage} alt="Union Attorney General Sign" className="h-8 object-contain" />
                  ) : (
                    <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန်</span>
                  )}
                  {formData.unionAttorneyGeneralSignatureImage && (
                    <button
                      type="button"
                      onClick={handleClearUnionAttorneyGeneralSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <span className="w-32 text-right text-xs mr-2">အမည်</span>
                <input type="text" name="unionAttorneyGeneralSignName" value={formData.unionAttorneyGeneralSignName} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
              </div>
              <div className="text-center text-xs font-bold pt-1">ပြည်ထောင်စုရှေ့နေချုပ်</div>
              <div className="flex items-center">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">ဆုံးဖြတ်သည့်ရက်စွဲ</span>
                <div className="flex-grow">{renderDatePicker('unionAttorneyGeneralSubmitDate')}</div>
                {/* <input type="text" name="deputyDirectorGeneralSubmitDate" value={formData.deputyDirectorGeneralSubmitDate} onChange={handleChange} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" /> */}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
                  {/* Form Action Buttons Component Integration */}
                    <FormActionButtons
                      isPreview={isPreview}
                      isEditable={isEditable}
                      isSubmitting={isSubmitting}
                      isFormSigned={isFormSigned}
                      isApproved={isApproved}
                      targetGeoLevels={formData.targetGeoLevels}
                      currentGeoLevel={currentUser?.scopeLevel}
                      selectedGeoLevels={selectedGeoLevels}
                      onGeoLevelChange={setSelectedGeoLevels}
                      onCancel={handleBackNavigation}
                      onSaveDraft={handleSaveDraft}
                      onApproveAndSave={handleApproveAndSave}
                      onSubmitToSuperior={handleSubmitToSuperior}
                    />
          
                  {/* Attachment Section */}
                  {/* <AttachmentSection
                    attachments={formData.attachments}
                    isEditable={isEditable}
                    isPreview={isPreview}
                    onFileChange={handleFileChange}
                    onRemoveFile={handleRemoveFile}
                  /> */}
          
                  {/* Signature Modal */}
                  <SignatureModal
                    isOpen={isSigning}
                    sigCanvasRef={sigCanvasRef}
                    onClose={() => {
                      setIsSigning(false);
                      setActiveSigningRole(null);
                    }}
                    onClear={() => sigCanvasRef.current?.clear()}
                    onSave={handleSaveSignature}
                  />

      </div>
      </PageLayoutWrapper>
    );
  }